import {useMutation} from '@apollo/client'
import {Button, Col, FloatingLabel, Form, Offcanvas, Row, Spinner} from 'react-bootstrap'
import {FormattedMessage, defineMessages, useIntl} from 'react-intl'
import {CREATE_NOTE_LEAD, EDIT_NOTE_LEAD} from '../../../../gql/mutations/leadMutations'
import {useEffect} from 'react'
import {toast} from 'react-toastify'
import {IResDataAddNote, IResDataEditNote} from '../../leadTypes'
import {NodeNotes} from '../sectionsTopNav/sectionNavTypes'
import {parseId} from '../../../../helpers'

interface IFormNoteProps {
  onBack: () => void
  leadId: string
  leadName: string
  infoEdit: Omit<NodeNotes, 'created' | 'user' | 'date'> | null
}

const requiredMark = '*'

const nM = defineMessages({
  titleAdd: {id: 'leads.note.titleAdd', defaultMessage: 'Add note - {name}'},
  titleEdit: {id: 'leads.note.titleEdit', defaultMessage: 'Edit note - {name}'},
  fieldTitle: {id: 'common.field.title', defaultMessage: 'Title'},
  field: {id: 'leads.note.field', defaultMessage: 'Note'},
  placeholder: {id: 'leads.note.placeholder', defaultMessage: 'Insert note'},
  toastAdded: {id: 'leads.note.toast.added', defaultMessage: 'Note added successfully'},
  toastEdited: {id: 'leads.note.toast.edited', defaultMessage: 'Note edited successfully'},
})

export const FormNote: React.FC<IFormNoteProps> = ({onBack, leadId, leadName, infoEdit}) => {
  const intl = useIntl()
  const errMsg = (message?: string) =>
    intl.formatMessage({id: 'common.error.withMessage', defaultMessage: 'Error: {message}'}, {message})
  const [gqlCreate, {data: dataNote, loading: loadingNote, error: errorNote}] =
    useMutation<IResDataAddNote>(CREATE_NOTE_LEAD)
  const [gqlEdit, {data: dataEditNote, loading: loadingEditNote, error: errorEditNote}] =
    useMutation<IResDataEditNote>(EDIT_NOTE_LEAD)

  useEffect(() => {
    if (errorNote) toast.error(errMsg(errorNote.message))
  }, [errorNote])
  useEffect(() => {
    if (errorEditNote) toast.error(errMsg(errorEditNote.message))
  }, [errorEditNote])

  useEffect(() => {
    if (!dataNote) return
    if (dataNote.createNote.success) {
      toast.success(intl.formatMessage(nM.toastAdded))
      onBack()
    } else if (dataNote.createNote?.errors?.length > 0) {
      toast.error(errMsg(dataNote.createNote.errors[0].message))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataNote])
  useEffect(() => {
    if (!dataEditNote) return
    if (dataEditNote.updateNote.success) {
      toast.success(intl.formatMessage(nM.toastEdited))
      onBack()
    } else if (dataEditNote.updateNote?.errors?.length > 0) {
      toast.error(errMsg(dataEditNote.updateNote.errors[0].message))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataEditNote])

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const {tittle, description} = Object.fromEntries(new FormData(e.currentTarget))
    if (!tittle || !description || !leadId) return
    const noteData = {
      tittle,
      description,
      lead: leadId,
    }
    if (infoEdit) {
      const variables = {
        id: parseId(infoEdit.id),
        noteData,
      }
      gqlEdit({variables})
    } else {
      gqlCreate({
        variables: {
          noteData,
        },
      })
    }
  }
  return (
    <>
      <Offcanvas.Header closeButton className='bg-light'>
        <div className='d-flex align-items-center justify-content-between w-100'>
          <Offcanvas.Title className='d-flex align-items-center gap-4'>
            {intl.formatMessage(infoEdit ? nM.titleEdit : nM.titleAdd, {name: leadName})}
          </Offcanvas.Title>
        </div>
      </Offcanvas.Header>
      <Offcanvas.Body>
        <form onSubmit={handleSubmit}>
          <div className='w-100 h-100'>
            <div>
              <Row>
                <Col className='col-12 mb-4'>
                  <FloatingLabel
                    controlId='tittle'
                    label={
                      <span>
                        <FormattedMessage id='common.field.title' defaultMessage='Title' /> <span style={{color: 'red'}}>{requiredMark}</span>
                      </span>
                    }
                    className='mb-3'
                  >
                    <Form.Control
                      placeholder={intl.formatMessage(nM.fieldTitle)}
                      name='tittle'
                      required
                      defaultValue={infoEdit?.tittle || undefined}
                    />
                  </FloatingLabel>
                </Col>
                <Col className='col-12 mb-4'>
                  <FloatingLabel
                    controlId='description'
                    label={
                      <span>
                        <FormattedMessage id='leads.note.field' defaultMessage='Note' /> <span style={{color: 'red'}}>{requiredMark}</span>
                      </span>
                    }
                    className='mb-3'
                  >
                    <Form.Control
                      placeholder={intl.formatMessage(nM.placeholder)}
                      as='textarea'
                      rows={4}
                      type='textarea'
                      name='description'
                      className='h-100'
                      required
                      defaultValue={infoEdit?.description || undefined}
                    />
                  </FloatingLabel>
                </Col>
              </Row>
            </div>

            <div className='d-flex algin-items-center justify-content-end gap-3 mt-4'>
              <Button type='button' variant='light' onClick={onBack}>
                <FormattedMessage id='common.action.cancel' defaultMessage='Cancel' />
              </Button>
              <Button type='submit' variant='primary' disabled={loadingNote || loadingEditNote}>
                {(loadingNote || loadingEditNote) && <Spinner size='sm' animation='border' />}
                <FormattedMessage id='common.action.save' defaultMessage='Save' />
              </Button>
            </div>
          </div>
        </form>
      </Offcanvas.Body>
    </>
  )
}
